import { isLocal, isPreview } from '@magicschool/utils/nextjs/metadata';
import type { CookieYesConsentCategory } from '@/declarations/cookieYes';

// CookieYes never loads locally. Set NEXT_PUBLIC_LOCAL_ANALYTICS_CONSENT_DENIED=true in .env.local
// to simulate denied consent for testing consent-gated integrations (Clarity, Amplitude).
const LOCAL_ANALYTICS_CONSENT_DENIED = process.env.NEXT_PUBLIC_LOCAL_ANALYTICS_CONSENT_DENIED === 'true';

/**
 * Reads the current CookieYes consent synchronously, with no subscription. Client-only — must
 * never run during SSR. Used by useCookieYesConsent's post-mount effect and by non-React callers
 * (e.g. analyticsClient.ts) that need a live read independent of whether a component using the
 * hook is mounted.
 */
export const getCurrentCookieYesConsent = (): Record<CookieYesConsentCategory, boolean> => {
  if (isLocal()) {
    return {
      necessary: true,
      functional: true,
      analytics: !LOCAL_ANALYTICS_CONSENT_DENIED,
      performance: true,
      advertisement: true,
      other: true,
    };
  }

  if (typeof window === 'undefined') {
    throw new Error('getCurrentCookieYesConsent is client-only and must never be called during SSR');
  }

  const consent = window.getCkyConsent?.()?.categories;
  if (consent) return consent;

  // getCkyConsent isn't defined yet (CDN script still loading). Preview's CookieYes env has a
  // domain mismatch and never resolves, so default to granted there — nothing at stake. In
  // production, default to denied (except `necessary`) until getCkyConsent resolves or a
  // cookieyes_banner_load/cookieyes_consent_update event supplies the real categories.
  const granted = isPreview();
  return {
    necessary: true,
    functional: granted,
    analytics: granted,
    performance: granted,
    advertisement: granted,
    other: granted,
  };
};
